import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { SkeletonLine } from '@/components/Skeleton';

type Granularity = 'hour' | 'day';

const AGENT_COLOR: Record<string, string> = {
  'rlp-hunter': '#0d9488',
  'rlp-extractor': '#2563eb',
  'property-scout': '#b45309',
  'outreach-writer': '#7c3aed',
};

function colorFor(agent: string, idx: number): string {
  return AGENT_COLOR[agent] ?? ['#0d9488', '#2563eb', '#b45309', '#7c3aed', '#db2777'][idx % 5]!;
}

export interface CostTimeseriesChartProps {
  granularity: Granularity;
  days: number;
}

export function CostTimeseriesChart({ granularity, days }: CostTimeseriesChartProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agents-cost-timeseries', granularity, days],
    queryFn: () => api.costTimeseries({ granularity, days }),
  });

  const chart = useMemo(() => (data ? computeChartModel(data.buckets, days, granularity) : null), [
    data,
    days,
    granularity,
  ]);

  if (isLoading) {
    return (
      <div className="card p-5">
        <SkeletonLine width="30%" />
        <div className="mt-4 h-40 rounded-lg bg-surface-sunk" />
      </div>
    );
  }
  if (isError || !chart) {
    return (
      <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
        Couldn't load cost time-series.
      </div>
    );
  }

  const { agents, maxY, bands, svgWidth, svgHeight, padding } = chart;
  if (agents.length === 0) {
    return (
      <div className="card p-5">
        <div className="text-[0.875rem] font-semibold text-ink">Cost over time</div>
        <div className="mt-2 text-[0.8125rem] text-ink-muted">No activity in the selected range.</div>
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[0.875rem] font-semibold text-ink">Cost over time</div>
          <div className="text-[0.75rem] text-ink-muted">
            {granularity === 'day' ? `Last ${days} days` : `Last ${days * 24} hours`} · by agent
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {agents.map((a, i) => (
            <div key={a.name} className="flex items-center gap-1.5 text-[0.75rem] text-ink-muted">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: colorFor(a.name, i) }}
                aria-hidden="true"
              />
              <span className="font-medium text-ink">{a.name}</span>
            </div>
          ))}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="none"
        className="mt-4 h-48 w-full"
        role="img"
        aria-label="Cost over time by agent"
      >
        {/* Horizontal grid */}
        {bands.map((y, i) => (
          <line
            key={i}
            x1={padding.left}
            x2={svgWidth - padding.right}
            y1={y}
            y2={y}
            stroke="#e2e8f0"
            strokeWidth={1}
          />
        ))}
        {/* Y-axis label at top */}
        <text
          x={padding.left}
          y={padding.top - 4}
          fontSize={10}
          fill="#64748b"
          fontFamily="ui-monospace, SFMono-Regular, monospace"
        >
          {`$${maxY.toFixed(2)}`}
        </text>
        {/* Lines per agent */}
        {agents.map((a, i) => (
          <path
            key={a.name}
            d={a.path}
            fill="none"
            stroke={colorFor(a.name, i)}
            strokeWidth={1.75}
            strokeLinejoin="round"
          />
        ))}
      </svg>
    </div>
  );
}

interface AgentSeries {
  name: string;
  path: string;
}

function computeChartModel(
  buckets: { t: number; agent: string; cost_usd: number }[],
  days: number,
  granularity: Granularity
) {
  const now = Date.now();
  const bucketMs = granularity === 'day' ? 86_400_000 : 3_600_000;
  const count = granularity === 'day' ? days : days * 24;
  const startMs = bucketStart(now - count * bucketMs, bucketMs);

  // Build axis timestamps.
  const axis: number[] = [];
  for (let i = 0; i < count; i++) axis.push(startMs + i * bucketMs);

  // Pivot: agent → bucket → cost.
  const byAgent = new Map<string, Map<number, number>>();
  for (const b of buckets) {
    const bt = bucketStart(b.t, bucketMs);
    if (bt < startMs) continue;
    const inner = byAgent.get(b.agent) ?? new Map<number, number>();
    inner.set(bt, (inner.get(bt) ?? 0) + b.cost_usd);
    byAgent.set(b.agent, inner);
  }

  let maxY = 0;
  for (const inner of byAgent.values()) {
    for (const v of inner.values()) if (v > maxY) maxY = v;
  }
  if (maxY === 0) maxY = 1; // avoid divide-by-zero; flat line at baseline

  const svgWidth = 600;
  const svgHeight = 180;
  const padding = { top: 14, right: 8, bottom: 18, left: 36 };
  const plotW = svgWidth - padding.left - padding.right;
  const plotH = svgHeight - padding.top - padding.bottom;
  const dx = count > 1 ? plotW / (count - 1) : plotW;
  const yOf = (v: number) => padding.top + plotH - (v / maxY) * plotH;
  const xOf = (i: number) => padding.left + i * dx;

  const agents: AgentSeries[] = [...byAgent.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, inner]) => {
      const parts: string[] = [];
      axis.forEach((t, i) => {
        const v = inner.get(t) ?? 0;
        parts.push(`${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(2)},${yOf(v).toFixed(2)}`);
      });
      return { name, path: parts.join(' ') };
    });

  const bands = [0.25, 0.5, 0.75, 1].map((f) => padding.top + plotH * (1 - f));
  return { agents, maxY, bands, svgWidth, svgHeight, padding };
}

function bucketStart(ms: number, bucketMs: number): number {
  return Math.floor(ms / bucketMs) * bucketMs;
}
